import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details'];

  connectedCallback() {
    super.connectedCallback();
    // Main menu elements
    this.container = this.querySelector('.menu-drawer-container');
    this.toggleButton = this.querySelector('#HeaderDrawerToggle');
    this.menuDrawer = this.querySelector('#HeaderDrawerMenuDrawer');
    this.headerDrawerContainer = this.querySelector('#HeaderDrawerContainer')
    // The backdrop is a pseudoelement (:before) of the body
    this.backdrop = document.body
    // State for main menu
    this._mainMenuOpen = false;
    // Bindings for main menu
    this._onMainToggleClick = this._onMainToggleClick.bind(this);
    this._onMainBackdropClick = this._onMainBackdropClick.bind(this);
    this._onMainKeyUp = this._onMainKeyUp.bind(this);
    // Listeners for main menu
    if (this.toggleButton) this.toggleButton.addEventListener('click', this._onMainToggleClick);
    if (this.backdrop && this.headerDrawerContainer) {
      // Add event listener to body
      this.backdrop.addEventListener('click', () => {
        if (this.backdrop && this.headerDrawerContainer) {
          this.backdrop.addEventListener('click', (event) => {
            if (this.headerDrawerContainer?.classList.contains('menu-open')) {
              // This will check the context of the click target. If you clicked inside the context of the headerDrawerContainer, it means you didn't click outside the menu
              const clickedInsideDrawer = event.target.closest('#HeaderDrawerContainer');

              // Close when clicked OUTSIDE the drawer (when closest returns null aka couldn't find the #HeaderDrawerContainer element)
              if (!clickedInsideDrawer) {
                this._onMainBackdropClick(event);
              }
            }
          });
        }

      });
    }
    this.addEventListener('keyup', this._onMainKeyUp);
    // --- Existing details/summary logic for submenus/accordions ---
    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.toggleButton) this.toggleButton.removeEventListener('click', this._onMainToggleClick);
    if (this.backdrop) this.backdrop.removeEventListener('click', this._onMainBackdropClick);
    this.removeEventListener('keyup', this._onMainKeyUp);
    this.removeEventListener('keyup', this.#onKeyUp);
  }

  // Main menu open state
  get mainMenuIsOpen() {
    return this._mainMenuOpen;
  }
  /**
   * Handle click on the main menu toggle button
   * @param {Event} e
   */
  _onMainToggleClick(e) {
    e.preventDefault();
    this.toggleMainMenu();
  }
  /**
   * Handle click on the main menu backdrop
   * @param {Event} e
   */
  _onMainBackdropClick(e) {
    e.preventDefault();
    this.closeMainMenu();
  }
  /**
   * Handle Escape key for main menu
   * @param {KeyboardEvent} event
   */
  _onMainKeyUp(event) {
    if (event.key === 'Escape' && this.mainMenuIsOpen) {
      this.closeMainMenu();
    }
  }
  /**
   * Toggle the main menu open/close state
   */
  toggleMainMenu() {
    this.mainMenuIsOpen ? this.closeMainMenu() : this.openMainMenu();
  }
  /**
   * Open the main menu drawer
   */
  openMainMenu() {
    if (this.mainMenuIsOpen) return;
    this._mainMenuOpen = true;
    if (this.container) this.container.classList.add('menu-open');
    if (this.menuDrawer) this.menuDrawer.setAttribute('aria-hidden', 'false');
    if (this.toggleButton) this.toggleButton.setAttribute('aria-expanded', 'true');
    document.documentElement.setAttribute('scroll-lock', '');
    setTimeout(() => {
      if (this.menuDrawer) trapFocus(this.menuDrawer);
    }, 0);
  }
  /**
   * Close the main menu drawer
   */
  closeMainMenu() {
    if (!this.mainMenuIsOpen) return;
    this._mainMenuOpen = false;
    if (this.container) this.container.classList.remove('menu-open');
    if (this.menuDrawer) this.menuDrawer.setAttribute('aria-hidden', 'true');
    if (this.toggleButton) this.toggleButton.setAttribute('aria-expanded', 'false');
    document.documentElement.removeAttribute('scroll-lock');
    removeTrapFocus();
  }

  // Existing details/summary logic for submenus/accordions below
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details && this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {Event} [event]
   */
  open(event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');
    requestAnimationFrame(() => {
      details.classList.add('menu-open');
      details.setAttribute('open', '');
      setTimeout(() => {
        trapFocus(details);
      }, 0);
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');

    onAnimationEnd(details, () => {
      reset(details);

      if (details === this.refs.details) {
        removeTrapFocus();
        const openDetails = this.querySelectorAll('details[open]');
        openDetails.forEach(reset);
      } else {
        setTimeout(() => {
          trapFocus(this.refs.details);
        }, 0);
      }
    });
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    /**
 * @param {AnimationEvent} event
 */
    function removeWillChangeOnAnimationEnd(event) {
      const target = event.target;
      if (target && target instanceof HTMLElement) {
        target.style.setProperty('will-change', 'unset');
        target.removeEventListener('animationend', removeWillChangeOnAnimationEnd);
      }
    }
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {Element} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
